import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import EmailOtpSignIn from "../auth/EmailOtpSignIn";

/**
 * Light, brand-matched sign-in panel for the Get Started flows.
 * Reuses the platform auth (Google redirect + Email OTP) — the same
 * registration used in the App Center store. Google is a full-page redirect
 * that returns to the current path, so callers should stash any flow state
 * before the user signs in with Google.
 */
const AuthPanel = ({
  title = "Create your account",
  subtitle = "Sign in or register to continue. It only takes a moment.",
  onSuccess,
  onBeforeGoogle,
}) => {
  const { signInWithGoogle, error } = useUser();
  const [localError, setLocalError] = useState(null);

  const handleGoogle = async () => {
    setLocalError(null);
    try {
      if (onBeforeGoogle) onBeforeGoogle();
      await signInWithGoogle();
    } catch (err) {
      setLocalError(err?.message || "Could not start Google sign-in.");
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-7">
        <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
        <p className="text-gray-500 text-sm mt-2">{subtitle}</p>
      </div>

      {(error || localError) && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{localError || error}</p>
        </div>
      )}

      <button
        onClick={handleGoogle}
        className="w-full flex items-center justify-center gap-3 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg py-3 px-4 transition-colors font-medium"
      >
        <svg width="20" height="20" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        <span>Continue with Google</span>
      </button>

      <div className="flex items-center gap-3 my-5">
        <div className="h-px flex-1 bg-gray-200" />
        <span className="text-xs font-medium text-gray-400">or</span>
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      <EmailOtpSignIn onSuccess={onSuccess} />

      <p className="mt-6 text-center text-gray-400 text-xs">
        By continuing you agree to our{" "}
        <Link to="/legal" className="text-[#5742bb] hover:underline">
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link to="/privacy" className="text-[#5742bb] hover:underline">
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
};

export default AuthPanel;
